import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  forkJoin,
  Observable,
  of,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { DataResult, GroupDescriptor, process, SortDescriptor } from '@progress/kendo-data-query';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faEdit,
  faHistory,
  faPlay,
  faToggleOff,
  faToggleOn,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Customer } from 'src/app/common/Customer';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { ProfileRoleConstants } from 'src/app/common/ProfileRole';
import {
  ScanProviderBinding,
  ScanProviderBindingPut,
} from 'src/app/common/ScanProviderBinding';
import { ScanRunStatusConstants } from 'src/app/common/ScanRun';
import { CustomerService } from 'src/app/services/customer.service';
import { AppProfileService } from 'src/app/services/profile.service';
import { ScanProviderBindingService } from 'src/app/services/scan-provider-binding.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

// Grid row: binding enriched with the customer's display name (the API only
// returns the raw customerId; names are joined client-side from /api/customer).
interface ScanProviderBindingRow extends ScanProviderBinding {
  customerName: string;
}

@Component({
  imports: [KENDO_GRID, FontAwesomeModule, RouterModule, AsyncClickDirective, GridNoRecordsComponent],
  selector: 'app-scan-provider-list',
  templateUrl: './scan-provider-list.component.html',
  styleUrls: ['./scan-provider-list.component.css'],
})
export class ScanProviderListComponent implements OnInit {
  private bindingService = inject(ScanProviderBindingService);
  private customerService = inject(CustomerService);
  private profileService = inject(AppProfileService);
  private apiActionService = inject(CallApiService);
  private toastr = inject(ToastrService);

  private destroyRef = inject(DestroyRef);

  public faEdit = faEdit;
  public faHistory = faHistory;
  public faPlay = faPlay;
  public faToggleOn = faToggleOn;
  public faToggleOff = faToggleOff;
  public faTrashAlt = faTrashAlt;

  public profile: ExtendedProfile;
  public isAdmin = false;

  public gridView: DataResult;
  public groups: GroupDescriptor[] = [{ field: 'customerName', dir: 'asc' }];
  public sort: SortDescriptor[] = [];

  private rows: ScanProviderBindingRow[] = [];
  private customers: Customer[] = [];
  private pendingBindingIds = new Set<string>();

  public ngOnInit() {
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      this.isAdmin = p?.role == ProfileRoleConstants.RoleIdAdmin;
      this.load();
    });
  }

  public onSortChange(sort: SortDescriptor[]) {
    this.sort = sort;
    this.refreshGrid();
  }

  public onToggleEnabled(row: ScanProviderBindingRow): () => Observable<unknown> {
    return () =>
      this.apiActionService
        .callWithToasts$(
          this.bindingService.update(row.id, this.toPutModel(row, !row.isEnabled)),
          row.isEnabled
            ? $localize`Scan-Provider wurde deaktiviert.`
            : $localize`Scan-Provider wurde aktiviert.`,
          $localize`Beim Aktualisieren der Scan-Provider-Zuordnung ist ein Fehler aufgetreten.`
        )
        .pipe(
          tap((updated) => {
            Object.assign(row, this.toRow(updated));
            this.refreshGrid();
          })
        );
  }

  // Runs are single-flight per binding (backend #505 E1): trigger-now answers 409
  // while a run is still Queued/Running. 202 and 409 both mean "a run is pending" —
  // disable the trigger button and poll the run history until it finishes.
  public onTriggerNow(row: ScanProviderBindingRow): () => Observable<unknown> {
    return () =>
      this.bindingService.triggerNow(row.id).pipe(
        tap({
          next: () => {
            this.toastr.success($localize`Scan wurde eingeplant.`);
            this.markPending(row.id);
          },
          error: (err) => {
            if (err?.status === 409) {
              this.toastr.info($localize`Für diese Zuordnung ist bereits ein Scan-Lauf eingeplant.`);
              this.markPending(row.id);
            } else {
              this.toastr.error($localize`Beim Einplanen des Scans ist ein Fehler aufgetreten.`);
            }
          },
        })
      );
  }

  public isRunPending(row: ScanProviderBindingRow): boolean {
    return this.pendingBindingIds.has(row.id);
  }

  public onDelete(row: ScanProviderBindingRow): () => Observable<unknown> {
    return () =>
      this.apiActionService
        .confirmAndCallWithToasts$(
          this.bindingService.delete(row.id),
          $localize`Soll die Scan-Provider-Zuordnung wirklich gelöscht werden?`,
          $localize`Scan-Provider-Zuordnung wurde gelöscht.`,
          $localize`Beim Löschen der Scan-Provider-Zuordnung ist ein Fehler aufgetreten.`
        )
        .pipe(
          tap(() => {
            this.rows = this.rows.filter((r) => r.id !== row.id);
            this.refreshGrid();
          })
        );
  }

  private load() {
    if (!this.profile?.isCarrier || !this.isAdmin) {
      return;
    }
    this.apiActionService.callWithToasts(
      forkJoin({
        bindings: this.bindingService.get(),
        customers: this.customerService.getAllForLookup(),
      }),
      null,
      $localize`Beim Laden der Scan-Provider-Zuordnungen ist ein Fehler aufgetreten.`,
      ({ bindings, customers }) => {
        this.customers = customers;
        this.rows = bindings.map((b) => this.toRow(b));
        this.refreshGrid();
      }
    );
  }

  private toRow(binding: ScanProviderBinding): ScanProviderBindingRow {
    return {
      ...binding,
      lastRunAt: binding.lastRunAt ? new Date(binding.lastRunAt) : null,
      nextRunAt: binding.nextRunAt ? new Date(binding.nextRunAt) : null,
      customerName:
        this.customers.find((c) => c.id === binding.customerId)?.fullName ?? binding.customerId,
    };
  }

  // Disables the row's trigger button and polls the run history (every 10 s, up to
  // 10 min) until no Queued/Running run remains; then re-enables the button and
  // refreshes the row so LastRun/NextRun reflect the finished run. A 404 during
  // polling (binding deleted meanwhile) just clears the flag.
  private markPending(bindingId: string) {
    if (this.pendingBindingIds.has(bindingId)) {
      return;
    }
    this.pendingBindingIds.add(bindingId);

    timer(10_000, 10_000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(60),
        switchMap(() => this.bindingService.getScanRuns(bindingId, 5).pipe(catchError(() => of([])))),
        filter(
          (runs) =>
            !runs.some(
              (r) =>
                r.status === ScanRunStatusConstants.Queued ||
                r.status === ScanRunStatusConstants.Running
            )
        ),
        take(1),
        switchMap(() => this.bindingService.getById(bindingId).pipe(catchError(() => of(null)))),
        // finalize (not the subscribe) clears the flag so the button also comes back
        // when the poll gives up after 10 min without the run finishing.
        finalize(() => this.pendingBindingIds.delete(bindingId))
      )
      .subscribe((binding) => {
        const row = this.rows.find((r) => r.id === bindingId);
        if (binding && row) {
          Object.assign(row, this.toRow(binding));
          this.refreshGrid();
        }
      });
  }

  // The PUT payload carries neither customerId nor providerKey — both immutable
  // server-side (backend #506 C4). Everything ELSE must ride along: the update is a
  // full replace, so an omitted field is a cleared field — the toggle used to silently
  // wipe expectedCurrency, categoryResolutionProfileKey and contentLanguage (resetting
  // a live shop back to the observing house default).
  private toPutModel(row: ScanProviderBinding, isEnabled: boolean): ScanProviderBindingPut {
    return {
      rootUrl: row.rootUrl,
      defaultProductCategoryId: row.defaultProductCategoryId,
      defaultVatId: row.defaultVatId,
      isEnabled: isEnabled,
      cadenceMinutes: row.cadenceMinutes,
      providerConfigJson: row.providerConfigJson,
      expectedCurrency: row.expectedCurrency,
      categoryResolutionProfileKey: row.categoryResolutionProfileKey,
      // #579 (slice 4): the VAT mirror — omitting it would reset a suggest/live shop to shadow.
      vatResolutionProfileKey: row.vatResolutionProfileKey,
      contentLanguage: row.contentLanguage,
    };
  }

  private refreshGrid() {
    this.gridView = process(this.rows, { group: this.groups, sort: this.sort });
  }
}
